import React, { useState, useEffect, useMemo, useRef } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Checkbox, Chip, Tooltip, Box } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid';
import { isEqual } from 'lodash';
import './EnvironmentalVariables.scss';

interface EnvironmentalVariablesProps {
  envVariables: Record<string, string> | null;
  isPending?: boolean;
  keyTooltips?: Record<string, React.ReactNode>;
  onChange?: (
    envVars: Record<string, string>,
    hasValidationErrors: boolean,
    hasEnvChanges: boolean,
  ) => void;
}

interface EnvVarRow {
  id: string;
  key: string;
  value: string;
  markedForDeletion: boolean;
  isNew?: boolean;
}

const createInitialRows = (envVars: Record<string, string> | null): EnvVarRow[] => {
  if (!envVars) {
    return [];
  }
  return Object.entries(envVars).map(([key, value]) => ({
    id: key,
    key,
    value: String(value),
    markedForDeletion: false,
    isNew: false,
  }));
};

const convertRowsToEnvVars = (rows: EnvVarRow[]): Record<string, string> => {
  const envVars: Record<string, string> = {};
  rows
    .filter((row) => !row.markedForDeletion)
    .forEach((row) => {
      const key = row.key.trim();
      const value = row.value.trim();
      if (key && value) {
        envVars[key] = value;
      }
    });
  return envVars;
};

export const EnvironmentalVariables: React.FC<EnvironmentalVariablesProps> = ({
  envVariables,
  isPending = false,
  keyTooltips,
  onChange,
}) => {
  const [rows, setRows] = useState<EnvVarRow[]>(() => createInitialRows(envVariables));
  const isInternalChange = useRef(false);
  const apiRef = useGridApiRef();

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    setRows(createInitialRows(envVariables));
  }, [envVariables]);

  const hasValidationErrors = useMemo(() => {
    const activeRows = rows.filter((row) => !row.markedForDeletion);
    const keys = activeRows.map((row) => row.key.trim());
    const duplicateKeys = keys.filter((key, index) => keys.indexOf(key) !== index);

    if (duplicateKeys.length > 0) return true;

    const hasEmptyFields = activeRows.some(
      (row) => row.key.trim() === '' || row.value.trim() === '',
    );

    return hasEmptyFields;
  }, [rows]);

  const hasEnvChanges = useMemo(() => {
    const initialRows = createInitialRows(envVariables);

    const hasNewRows = rows.some((row) => row.isNew);
    const hasDeletedRows = rows.some((row) => row.markedForDeletion);

    if (hasNewRows || hasDeletedRows) return true;

    const currentEnvVars = convertRowsToEnvVars(rows);
    const initialEnvVars = convertRowsToEnvVars(initialRows);

    return !isEqual(currentEnvVars, initialEnvVars);
  }, [rows, envVariables]);

  useEffect(() => {
    if (!onChange) return;

    const envVars = convertRowsToEnvVars(rows);
    isInternalChange.current = true;
    onChange(envVars, hasValidationErrors, hasEnvChanges);
  }, [rows, hasValidationErrors, hasEnvChanges]);

  const updateRow = (id: string, updates: Partial<EnvVarRow>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...updates } : row)));
  };

  const handleProcessRowUpdate = (newRow: GridRowModel): GridRowModel => {
    updateRow(newRow.id as string, newRow as Partial<EnvVarRow>);
    return newRow;
  };

  const handleToggleDeletion = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (row) {
      updateRow(id, { markedForDeletion: !row.markedForDeletion });
    }
  };

  const handleAddRow = () => {
    const newId = `new_${Date.now()}`;
    setRows((prev) => [
      ...prev,
      {
        id: newId,
        key: '',
        value: '',
        markedForDeletion: false,
        isNew: true,
      },
    ]);

    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.startCellEditMode({ id: newId, field: 'key' });
      }
    }, 100);
  };

  const columns: GridColDef[] = [
    {
      headerName: 'Environmental Variable',
      field: 'key',
      flex: 1,
      sortable: true,
      editable: true,
      renderCell: (params: GridRenderCellParams<EnvVarRow>) => {
        const tooltip = keyTooltips?.[params.row.key];
        if (!tooltip) return <span>{params.row.key}</span>;

        return (
          <Box className="env-vars-keyCell">
            <span className="env-vars-keyCell-text">{params.row.key}</span>
            <Tooltip
              title={tooltip}
              placement="top"
              arrow
              slotProps={{
                tooltip: {
                  className: 'env-vars-tooltip',
                },
                arrow: { className: 'env-vars-tooltipArrow' },
              }}
            >
              <span className="env-vars-keyCell-icon">
                <InfoOutlinedIcon className="env-vars-keyCell-iconSvg" />
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      headerName: 'Value',
      field: 'value',
      flex: 1.5,
      sortable: false,
      editable: true,
    },
    {
      headerName: 'Delete',
      field: 'markedForDeletion',
      flex: 0.8,
      sortable: false,
      renderCell: (params: GridRenderCellParams<EnvVarRow>) => (
        <Checkbox
          checked={params.row.markedForDeletion}
          onChange={() => handleToggleDeletion(params.row.id)}
          color="primary"
          disabled={params.row.isNew}
        />
      ),
    },
  ];

  return (
    <div className="environmental-variables-container">
      <div className="environmental-variables-actions">
        <div className="environmental-variables-header">
          <div className="environmental-variables-title">Environment Variables</div>
          {isPending && (
            <Tooltip title="These variables are not yet applied to the device.">
              <Chip label="Pending" color="warning" size="small" />
            </Tooltip>
          )}
        </div>
        <Button variant="outlined" color="primary" onClick={handleAddRow}>
          + Add Variable
        </Button>
      </div>
      <div className="data-grid-container">
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          autoHeight
          processRowUpdate={handleProcessRowUpdate}
          onProcessRowUpdateError={(error) => console.error('Row update error:', error)}
        />
      </div>
    </div>
  );
};
